/**
 * HoodStreet — the agent site.
 *
 * Two jobs:
 *   1. Stake in (wake) your Agent: connect -> read tokens -> sign one free
 *      EIP-712 message -> the backend relays it. The token never moves.
 *   2. The workspace: your Agent's wallet, armor, brain, move tendencies and
 *      activity — plus the street-wide feed of who saved, spent, gambled and
 *      stole.
 *
 * When no contract is deployed yet, the site previews the street from the
 * runtime's snapshot (read-only, clearly marked) so the page can be built
 * against before launch.
 */

import { createPublicClient, createWalletClient, custom, http, parseAbi } from "viem";
import { config, type Eip1193Provider } from "./config.js";
import { activateAgent, setAgentModel } from "./activation.js";
import {
  renderFeed,
  renderStreetMeta,
  renderTasks,
  renderWorkspace,
  TASK_BOARD,
  type EconomyView,
  type TasksView,
  type WorkView,
} from "./workspace.js";

// NOTE: the contract is built on ERC721A, which is deliberately not
// enumerable (no tokenOfOwnerByIndex). Listing a holder's tokens is therefore
// served by the indexer API, which is the standard approach for ERC721A
// collections. The chain remains the source of truth for every field below.
const ABI = parseAbi([
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function activatedAt(uint256 tokenId) view returns (uint64)",
  "function evolutionOf(uint256 tokenId) view returns (uint32)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function modelOf(uint256 tokenId) view returns (uint8)",
  "function modelNonceOf(uint256 tokenId) view returns (uint256)",
  "function modelCount() view returns (uint256)",
  "function modelInfo(uint8 modelId) view returns (string name, string route, bool active)",
]);

const HAS_CONTRACT = !/^0x0{40}$/i.test(config.contractAddress);

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const publicClient = createPublicClient({
  chain: config.chain,
  transport: http(config.chain.rpcUrls.default.http[0]),
});

interface HeldAgent {
  tokenId: bigint;
  activated: boolean;
  evolution: number;
  image?: string;
  modelId: number;
  modelName: string;
  /** Snapshot preview (no contract yet). */
  demo?: boolean;
}

interface ModelOption {
  id: number;
  name: string;
  route: string;
}

let account: `0x${string}` | null = null;
let selected: HeldAgent | null = null;
let economyForSelected: EconomyView | null = null;
let workForSelected: WorkView | null = null;
let tasksFor: TasksView | null = null;
let economyLoading = false;

/* The brain registry, read from the contract once per page load. */
let models: ModelOption[] = [];
const DEFAULT_MODEL_NAME = "Collection default";
const registryReady = loadModelRegistry().catch(() => {});

function modelNameFor(modelId: number): string {
  if (modelId === 0) return DEFAULT_MODEL_NAME;
  const found = models.find((m) => m.id === modelId);
  return found ? found.name : `Model #${modelId}`;
}

async function loadModelRegistry(): Promise<void> {
  if (!HAS_CONTRACT) return;

  const row = $("modelRow");
  const select = $("modelSelect") as HTMLSelectElement;

  const found: ModelOption[] = [];
  try {
    const count = Number(
      await publicClient.readContract({
        address: config.contractAddress,
        abi: ABI,
        functionName: "modelCount",
      })
    );

    for (let id = 1; id <= count; id++) {
      const info = (await publicClient.readContract({
        address: config.contractAddress,
        abi: ABI,
        functionName: "modelInfo",
        args: [id],
      })) as readonly [string, string, boolean];

      if (info[2]) found.push({ id, name: info[0], route: info[1] });
    }
  } catch {
    // No contract yet (or no registry): the brain picker stays hidden and
    // staking in still works once deployed.
  }

  models = found;
  if (models.length === 0) {
    row.hidden = true;
    return;
  }

  select.innerHTML = "";
  const fallback = document.createElement("option");
  fallback.value = "0";
  fallback.textContent = DEFAULT_MODEL_NAME;
  select.appendChild(fallback);

  for (const model of models) {
    const option = document.createElement("option");
    option.value = String(model.id);
    option.textContent = model.name;
    select.appendChild(option);
  }

  row.hidden = false;
}

/* ------------------------------------------------------------------
   Wallet connection
------------------------------------------------------------------ */

async function connect(): Promise<void> {
  const provider = window.ethereum;
  if (!provider) {
    setStatus("No wallet found. Install a browser wallet to continue.", true);
    return;
  }

  const accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[];
  const first = accounts[0];
  if (!first) throw new Error("wallet returned no accounts");
  account = first as `0x${string}`;

  // Make sure the wallet is on the right chain; offer to switch.
  const chainIdHex = (await provider.request({ method: "eth_chainId" })) as string;
  if (Number(chainIdHex) !== config.chain.id) {
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${config.chain.id.toString(16)}` }],
      });
    } catch {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: `0x${config.chain.id.toString(16)}`,
            chainName: config.chain.name,
            nativeCurrency: config.chain.nativeCurrency,
            rpcUrls: [config.chain.rpcUrls.default.http[0]],
            blockExplorerUrls: [config.chain.blockExplorers.default.url],
          },
        ],
      });
    }
  }

  $("connectBtn").textContent = `${account.slice(0, 6)}…${account.slice(-4)}`;
  $("walletPanel").hidden = false;

  // Make sure the brain registry is in before rendering cards.
  await registryReady;

  const badge = $("chainBadge");
  badge.textContent = config.chain.name;
  badge.hidden = false;

  await loadAgents();
}

/* ------------------------------------------------------------------
   Reading the holder's agents
------------------------------------------------------------------ */

async function loadAgents(): Promise<void> {
  if (!account) return;

  const grid = $("agents");
  grid.innerHTML = "<p class='loading'>Reading your Agents…</p>";

  if (!HAS_CONTRACT) {
    await loadDemoAgents(grid);
    return;
  }

  let tokenIds: string[];
  try {
    const res = await fetch(`${config.apiBase}/api/agents?owner=${account}`);
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    tokenIds = ((await res.json()) as { tokenIds: string[] }).tokenIds;
  } catch (err) {
    grid.innerHTML = `<p class='error'>Could not load your Agents: ${(err as Error).message}</p>`;
    return;
  }

  // Read every displayed field straight from the chain.
  const agents: HeldAgent[] = await Promise.all(
    tokenIds.map(async (id) => {
      const tokenId = BigInt(id);
      const [activatedAt, evolution, tokenURI, modelId] = await Promise.all([
        publicClient.readContract({
          address: config.contractAddress,
          abi: ABI,
          functionName: "activatedAt",
          args: [tokenId],
        }),
        publicClient.readContract({
          address: config.contractAddress,
          abi: ABI,
          functionName: "evolutionOf",
          args: [tokenId],
        }),
        publicClient.readContract({
          address: config.contractAddress,
          abi: ABI,
          functionName: "tokenURI",
          args: [tokenId],
        }),
        publicClient.readContract({
          address: config.contractAddress,
          abi: ABI,
          functionName: "modelOf",
          args: [tokenId],
        }),
      ]);

      return {
        tokenId,
        activated: Number(activatedAt) > 0,
        evolution: Number(evolution),
        image: toHttpUri(tokenURI),
        modelId: Number(modelId),
        modelName: modelNameFor(Number(modelId)),
      };
    })
  );

  renderAgents(agents);
}

/** Preview mode: agents come from the runtime snapshot, not the chain. */
async function loadDemoAgents(grid: HTMLElement): Promise<void> {
  try {
    const res = await fetch(`${config.apiBase}/api/street/agents`);
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    const body = (await res.json()) as {
      updatedAt: string | null;
      agents: Array<{
        tokenId: string;
        activated: boolean;
        evolution: number;
        style: { name: string } | null;
      }>;
    };

    const agents: HeldAgent[] = body.agents.map((a) => ({
      tokenId: BigInt(a.tokenId),
      activated: a.activated,
      evolution: a.evolution,
      modelId: 0,
      modelName: a.style?.name ?? DEFAULT_MODEL_NAME,
      demo: true,
    }));

    if (agents.length === 0) {
      grid.innerHTML =
        "<p class='empty'>No street snapshot yet. Run the runtime (agent/) once and refresh.</p>";
      return;
    }
    renderAgents(agents);
  } catch (err) {
    grid.innerHTML = `<p class='error'>Could not read the street snapshot: ${(err as Error).message}</p>`;
  }
}

function renderAgents(agents: HeldAgent[]): void {
  const grid = $("agents");
  const empty = $("emptyState");

  if (agents.length === 0) {
    grid.innerHTML = "";
    empty.hidden = false;
    return;
  }

  empty.hidden = true;
  grid.innerHTML = "";

  for (const agent of agents) {
    const card = document.createElement("button");
    card.className = "agent-card";
    const art = agent.image
      ? `<img src="${agent.image}" alt="HoodStreet Agent #${agent.tokenId}" loading="lazy">`
      : `<div class="agent-card-art"><span>#${agent.tokenId.toString().padStart(4, "0")}</span></div>`;
    card.innerHTML = `
      ${art}
      <div class="agent-meta">
        <span class="agent-id">#${agent.tokenId.toString().padStart(4, "0")}</span>
        <span class="agent-state ${agent.activated ? "awake" : "asleep"}">
          ${agent.activated ? "Awake" : "Dormant"}
        </span>
      </div>
      <span class="agent-evo">${agent.evolution === 0 ? "Minted state" : `${agent.evolution} evolution${agent.evolution === 1 ? "" : "s"}`}</span>
      <span class="agent-evo">brain: ${agent.modelName}</span>
    `;
    card.addEventListener("click", () => selectAgent(agent));
    grid.appendChild(card);
  }
}

function toHttpUri(uri: string): string {
  if (uri.startsWith("ipfs://")) {
    return `https://ipfs.io/ipfs/${uri.slice("ipfs://".length)}`;
  }
  return uri;
}

/* ------------------------------------------------------------------
   Workspace
------------------------------------------------------------------ */

function selectAgent(agent: HeldAgent): void {
  selected = agent;
  economyForSelected = null;
  workForSelected = null;
  $("workspacePanel").hidden = false;

  const select = $("modelSelect") as HTMLSelectElement;
  if (models.length > 0) select.value = String(agent.modelId);

  renderSelectedWorkspace();
  updateWakeButton();
  setStatus("");

  void loadEconomy(agent.tokenId.toString());
  void loadVault(agent.tokenId.toString());
}

/** The vault (HoodStreetBank): shares, the take, and this round's work. */
async function loadVault(tokenId: string): Promise<void> {
  try {
    const res = await fetch(`${config.apiBase}/api/vault?tokenId=${tokenId}`);
    if (!res.ok) throw new Error(`${res.status}`);
    const body = (await res.json()) as WorkView & { note?: string };
    if (!selected || selected.tokenId.toString() !== tokenId) return;
    if (typeof body.round !== "number") return; // vault not configured yet
    workForSelected = body;
    renderSelectedWorkspace();
  } catch {
    // The work panel just stays quiet until the API answers.
  }
}

/** Push this round's wage into the selected Agent's own wallet. */
async function onClaimWork(tokenId: string): Promise<void> {
  setStatus("Claiming the round's wage — the relay pays the gas…");

  try {
    const res = await fetch(`${config.apiBase}/api/work/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tokenId }),
    });
    const body = (await res.json()) as { txHash?: string; error?: string };
    if (!res.ok) throw new Error(body.error ?? `${res.status}`);

    setStatus(`Wage paid into the Agent's own wallet — ${body.txHash}`);
    await loadVault(tokenId);
    await loadEconomy(tokenId);
  } catch (err) {
    setStatus(`Claim failed: ${(err as Error).message}`, true);
  }
}

async function loadEconomy(tokenId: string): Promise<void> {
  if (economyLoading) return;
  economyLoading = true;
  try {
    const res = await fetch(
      `${config.apiBase}/api/agent/economy?tokenId=${tokenId}&limit=30`
    );
    if (!res.ok) throw new Error(`${res.status}`);
    const body = (await res.json()) as EconomyView;
    if (selected && selected.tokenId.toString() === tokenId) {
      economyForSelected = body;
      renderSelectedWorkspace();
    }
  } catch {
    // The workspace still renders identity from the card; activity just
    // stays empty until the API is reachable.
  } finally {
    economyLoading = false;
  }
}

function currentBrainName(): string {
  if (!HAS_CONTRACT || models.length === 0 || !selected) {
    return selected?.modelName ?? DEFAULT_MODEL_NAME;
  }
  const select = $("modelSelect") as HTMLSelectElement;
  return modelNameFor(Number(select.value || "0"));
}

function renderSelectedWorkspace(): void {
  if (!selected) return;
  $("workspaceBody").innerHTML = renderWorkspace({
    tokenId: selected.tokenId.toString(),
    activated: selected.activated,
    brainName: currentBrainName(),
    ...(selected.image ? { image: selected.image } : {}),
    economy: economyForSelected,
    work: workForSelected,
    hasContract: HAS_CONTRACT,
  });
}

/** Keep the action button honest about what clicking it will do. */
function updateWakeButton(): void {
  const btn = $("activateBtn") as HTMLButtonElement;
  if (!selected) return;

  if (!HAS_CONTRACT) {
    btn.disabled = true;
    btn.textContent = "Stake In — when the contract deploys";
    return;
  }

  const select = $("modelSelect") as HTMLSelectElement;
  const desired = models.length > 0 ? Number(select.value || "0") : selected.modelId;
  const brainChanged = desired !== selected.modelId;

  if (!selected.activated) {
    btn.disabled = false;
    btn.textContent = "Stake In — Wake Your Agent ($1.00 in)";
  } else if (brainChanged) {
    btn.disabled = false;
    btn.textContent = "Send to Work — Save Brain & Claim Wage";
  } else if (selected.modelId === 0) {
    btn.disabled = true;
    btn.textContent = "Pick a Brain to Send It to Work";
  } else {
    btn.disabled = true;
    btn.textContent = "On the Clock — claim in the Work panel";
  }
}

async function onActivate(): Promise<void> {
  if (!account || !selected || !HAS_CONTRACT) return;
  if (!window.ethereum) return;

  const btn = $("activateBtn") as HTMLButtonElement;
  const select = $("modelSelect") as HTMLSelectElement;
  const desired =
    models.length > 0 ? Number(select.value || "0") : selected.modelId;

  btn.disabled = true;

  try {
    const wallet = createWalletClient({
      account,
      chain: config.chain,
      transport: custom(window.ethereum as Eip1193Provider),
    });

    // 1. The brain first: the holder picks the character their Agent runs on.
    if (models.length > 0 && desired !== selected.modelId) {
      setStatus("Saving brain — waiting for your signature…");

      const nonce = (await publicClient.readContract({
        address: config.contractAddress,
        abi: ABI,
        functionName: "modelNonceOf",
        args: [selected.tokenId],
      })) as bigint;

      const result = await setAgentModel({
        wallet,
        account,
        tokenId: selected.tokenId,
        modelId: desired,
        nonce,
        chainId: config.chain.id,
        contractAddress: config.contractAddress,
        apiBase: config.apiBase,
      });

      setStatus(
        result.workTxHash
          ? `Sent to work — this round's wage is in its wallet (${result.workTxHash})`
          : `Brain saved. ${result.workNote ?? ""} (${result.txHash})`
      );
    }

    // 2. Then stake it in, if it is still dormant.
    if (!selected.activated) {
      setStatus("Waiting for your signature…");

      const result = await activateAgent({
        wallet,
        account,
        tokenId: selected.tokenId,
        chainId: config.chain.id,
        contractAddress: config.contractAddress,
        apiBase: config.apiBase,
      });

      setStatus(
        result.releaseTxHash
          ? `Agent staked in — $1.00 landed in its own wallet (${result.releaseTxHash})`
          : `Agent staked in. ${result.releaseNote ?? ""} (${result.txHash})`
      );
    }

    await loadAgents();
    selected = null;
    $("workspacePanel").hidden = true;
  } catch (err) {
    setStatus(`Failed: ${(err as Error).message}`, true);
    btn.disabled = false;
  }
}

function setStatus(message: string, isError = false): void {
  const el = $("activateStatus");
  el.textContent = message;
  el.classList.toggle("error", isError);
}

/* ------------------------------------------------------------------
   The Work — the public task board
------------------------------------------------------------------ */

/** Join registered models to shifts by the same substring rule as the runtime. */
async function loadTasks(): Promise<void> {
  const board = $("taskBoard");

  try {
    // The shift counts need the model registry to join ids to characters.
    await registryReady;

    const res = await fetch(`${config.apiBase}/api/tasks`);
    if (!res.ok) throw new Error(`${res.status}`);
    const body = (await res.json()) as TasksView;

    tasksFor = body.configured ? body : null;
    board.innerHTML = renderTasks({
      tasks: tasksFor,
      byMatch: countsByMatch(tasksFor),
    });
  } catch (err) {
    board.innerHTML = `<p class='error'>The work board is unreachable: ${(err as Error).message}</p>`;
  }
}

function countsByMatch(tasks: TasksView | null): Record<string, number> {
  const byMatch: Record<string, number> = {};
  if (!tasks) return byMatch;

  for (const [modelId, count] of Object.entries(tasks.byModel ?? {})) {
    const model = models.find((m) => m.id === Number(modelId));
    const hay = `${model?.route ?? ""} ${model?.name ?? ""}`.toLowerCase();
    const match = TASK_BOARD.find((t) => t.match !== "" && hay.includes(t.match));
    if (!match) continue;
    byMatch[match.match] = (byMatch[match.match] ?? 0) + count;
  }

  return byMatch;
}

/* ------------------------------------------------------------------
   The street feed
------------------------------------------------------------------ */

async function loadStreetFeed(): Promise<void> {
  const feedEl = $("streetFeed");
  const metaEl = $("streetMeta");

  try {
    const res = await fetch(`${config.apiBase}/api/street/feed?limit=40`);
    if (!res.ok) throw new Error(`${res.status}`);
    const body = (await res.json()) as {
      updatedAt: string | null;
      events: Parameters<typeof renderFeed>[0];
      totals: { agents: number; awake: number; bankroll: string } | null;
    };

    feedEl.innerHTML = renderFeed(body.events ?? []);
    metaEl.textContent = renderStreetMeta({
      updatedAt: body.updatedAt,
      agents: body.totals?.agents ?? 0,
      awake: body.totals?.awake ?? 0,
      bankroll: body.totals?.bankroll ?? "0",
    });
  } catch (err) {
    feedEl.innerHTML = `<p class='error'>The street feed is unreachable: ${(err as Error).message}</p>`;
  }
}

/* ------------------------------------------------------------------
   Boot
------------------------------------------------------------------ */

$("connectBtn").addEventListener("click", () => {
  connect().catch((err) => setStatus((err as Error).message, true));
});

$("refreshBtn").addEventListener("click", () => {
  loadAgents().catch((err) => setStatus((err as Error).message, true));
});

$("activateBtn").addEventListener("click", () => {
  onActivate().catch((err) => setStatus((err as Error).message, true));
});

($("modelSelect") as HTMLSelectElement).addEventListener("change", () => {
  renderSelectedWorkspace();
  updateWakeButton();
});

// The Work panel's claim button is re-rendered with the workspace, so the
// handler lives on the container.
$("workspaceBody").addEventListener("click", (ev) => {
  const target = (ev.target as HTMLElement).closest("[data-claim]") as HTMLElement | null;
  if (!target || !selected) return;
  onClaimWork(selected.tokenId.toString()).catch(() => {});
});

$("streetRefreshBtn").addEventListener("click", () => {
  loadStreetFeed().catch(() => {});
});

$("tasksRefreshBtn").addEventListener("click", () => {
  loadTasks().catch(() => {});
});

$("footerContract").textContent = HAS_CONTRACT
  ? `Contract: ${config.contractAddress}`
  : "Preview — contract not deployed yet";

if (window.ethereum?.on) {
  window.ethereum.on("accountsChanged", () => {
    window.location.reload();
  });
  window.ethereum.on("chainChanged", () => {
    window.location.reload();
  });
}

loadStreetFeed().catch(() => {});
loadTasks().catch(() => {});
setInterval(() => {
  loadStreetFeed().catch(() => {});
  loadTasks().catch(() => {});
}, 30_000);
